import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';
/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.#colorPicker = undefined;
    this.#colorPosition = 0;
    this.#mediaColors.clear();
    this.#galleryRequestedColor = null;

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const excludedContexts =
      'dialog, product-card, quick-add-component, quick-add-dialog, swatches-variant-picker-component';

    if (this.closest(excludedContexts) || !target || !this.slideshow) return;

    const mapElement = Array.from(this.children).find(
      (element) =>
        element.matches('script[type="application/json"][data-gallery-color-map]')
    );

    if (!mapElement) return;

    try {
      const data = JSON.parse(mapElement.textContent || '{}');
      const productId = String(data.productId || '');
      const colorPosition = Number(data.colorPosition);

      if (
        !productId ||
        ![1, 2, 3].includes(colorPosition) ||
        !Array.isArray(data.variants)
      ) return;

      const pickers = Array.from(
        target.querySelectorAll('variant-picker[data-template-product-match="true"]')
      ).filter(
        (picker) =>
          picker.dataset.productId === productId &&
          picker.closest('.shopify-section, dialog') === target &&
          !picker.closest(excludedContexts) &&
          typeof picker.updateSelectedOption === 'function'
      );

      if (pickers.length !== 1) return;

      const owners = new Map();

      for (const variant of data.variants) {
        if (
          !variant ||
          variant.mediaId == null ||
          String(variant.mediaId) === '' ||
          typeof variant.color !== 'string' ||
          variant.color === ''
        ) continue;

        const mediaId = String(variant.mediaId);

        if (!owners.has(mediaId)) {
          owners.set(mediaId, new Set());
        }

        owners.get(mediaId).add(variant.color);
      }

      this.#colorPicker = pickers[0];
      this.#colorPosition = colorPosition;

      for (const [mediaId, colors] of owners) {
        if (colors.size === 1) {
          this.#mediaColors.set(mediaId, colors.values().next().value);
        }
      }

      this.slideshow.addEventListener(
        SlideshowSelectEvent.eventName,
        this.#handleGallerySelection,
        { signal }
      );

      for (const eventName of ['pointerdown', 'touchstart', 'keydown', 'wheel']) {
        this.slideshow.addEventListener(
          eventName,
          this.#allowCustomerNavigation,
          { signal, capture: true, passive: true }
        );
      }
    } catch (error) {
      this.#colorPicker = undefined;
      this.#colorPosition = 0;
      this.#mediaColors.clear();
      console.warn('Gallery color synchronization unavailable:', error);
    }
  }

#controller = new AbortController();
#colorPicker;
#colorPosition = 0;
#mediaColors = new Map();
#suppressGallerySelection = false;
#galleryRequestedColor = null;

#allowCustomerNavigation = (event) => {
  if (event.isTrusted) this.#suppressGallerySelection = false;
};

#handleGallerySelection = (event) => {
  if (
    event.target !== this.slideshow ||
    !event.detail?.userInitiated ||
    this.#suppressGallerySelection ||
    !this.#colorPicker?.isConnected
  ) return;

  const color = this.#mediaColors.get(String(event.detail.id));
  if (color === undefined) return;

  const controls = Array.from(
    this.#colorPicker.querySelectorAll(
      'input[type="radio"][data-input-id], option[data-input-id]'
    )
  );

  const control = controls.find((element) =>
    element.dataset.inputId?.startsWith(`${this.#colorPosition}-`) &&
    element.value === color
  );

  if (!control) return;

  const alreadySelected =
    control instanceof HTMLInputElement
      ? control.checked
      : control.parentElement instanceof HTMLSelectElement &&
        control.parentElement.value === control.value;

  if (alreadySelected) return;

  this.#galleryRequestedColor = color;
  this.#colorPicker.updateSelectedOption(control);

  const changeTarget =
    control instanceof HTMLOptionElement ? control.parentElement : control;

  changeTarget?.dispatchEvent(new Event('change', { bubbles: true }));
};
  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  //   #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   // PDP: No change needed here; perform a standard replace
  //   this.replaceWith(newMediaGallery);
  // };
#handleVariantUpdate = (event) => {
  if (
    this.#colorPicker &&
    String(event.detail.data?.productId) !== String(this.#colorPicker.dataset.productId)
  ) return;

  const variantResource = event.detail.resource;

  // Keep the customer's chosen gallery image when the requested color resolves.
  if (this.#galleryRequestedColor !== null) {
    const selectedColor = Array.from(
      this.#colorPicker.querySelectorAll(
        'input[type="radio"][data-input-id], option[data-input-id]'
      )
    ).find((element) =>
      element.dataset.inputId?.startsWith(`${this.#colorPosition}-`) &&
      (
        element instanceof HTMLInputElement
          ? element.checked
          : element.parentElement instanceof HTMLSelectElement &&
            element.parentElement.value === element.value
      )
    );

    if (selectedColor?.value === this.#galleryRequestedColor) {
      this.#galleryRequestedColor = null;
      return;
    }
  }

  if (!variantResource || !this.slideshow) {
    console.log('🖼️ Media Gallery: Missing variant resource or slideshow');
    return;
  }

  const featuredMediaId = variantResource.featured_media?.id;
  console.log('🖼️ Media Gallery: Featured media ID:', featuredMediaId);

  if (featuredMediaId) {
    this.#suppressGallerySelection = true;
    this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
  } else {
    console.log('🖼️ Media Gallery: No featured media ID found');
  }
};
  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
#handleZoomMediaSelected = async (event) => {
  this.#suppressGallerySelection = true;
  this.slideshow?.select(event.detail.index, undefined, { animate: false });
};
  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
